<template>
  <div class="driver-invoice-detail">
    <!-- Loading -->
    <b-card v-if="loading">
      <div class="text-center py-5">
        <b-spinner variant="primary" />
        <p class="mt-2 text-muted">
          Chargement de la facture...
        </p>
      </div>
    </b-card>

    <!-- Not Found -->
    <b-card v-else-if="!invoice">
      <div class="text-center py-5">
        <feather-icon
          icon="AlertCircleIcon"
          size="48"
          class="text-danger"
        />
        <p class="mt-2 text-muted">
          Facture non trouvée
        </p>
        <b-button
          variant="primary"
          :to="{ name: 'driver-invoice-list' }"
        >
          Retour à la liste
        </b-button>
      </div>
    </b-card>

    <template v-else>
      <!-- Dedicated A4 print header -->
      <section class="invoice-print-document-header">
        <div class="invoice-print-title-row">
          <div>
            <div class="invoice-print-kicker">
              Facture livreur
            </div>
            <h1>{{ invoice.invoice_number }}</h1>
            <div class="invoice-print-reference">
              Référence {{ invoice.reference }} · {{ formatDate(invoice.created_at) }}
            </div>
          </div>
          <div class="invoice-print-status">
            {{ statusLabel(invoice.status) }}
          </div>
        </div>

        <div class="invoice-print-parties">
          <div class="invoice-print-party invoice-print-company">
            <img
              src="@/assets/images/logo/logo.png"
              alt="Team Parcel Express"
              class="invoice-print-logo"
            >
            <h2>{{ company.name || 'Team Parcel Express' }}</h2>
            <p v-if="company.tax_identification_number">
              <strong>Matricule fiscal :</strong> {{ company.tax_identification_number }}
            </p>
            <p v-if="company.address">
              <strong>Adresse :</strong> {{ company.address }}
            </p>
            <p v-if="company.phone1">
              <strong>Téléphone :</strong> {{ company.phone1 }}<template v-if="company.phone2">
                / {{ company.phone2 }}
              </template>
            </p>
            <p v-if="company.email">
              <strong>Email :</strong> {{ company.email }}
            </p>
          </div>

          <div class="invoice-print-party invoice-print-driver">
            <div class="invoice-print-party-label">
              Informations du livreur
            </div>
            <h2>{{ invoice.driver_name || invoice.driver_username || '-' }}</h2>
            <p v-if="invoice.driver_phone">
              <strong>Téléphone :</strong> {{ invoice.driver_phone }}
            </p>
            <p v-if="invoice.driver_email">
              <strong>Email :</strong> {{ invoice.driver_email }}
            </p>
            <p v-if="invoice.driver_address">
              <strong>Adresse :</strong> {{ invoice.driver_address }}
            </p>
          </div>
        </div>
      </section>

      <!-- Header -->
      <b-card class="mb-2 invoice-print-header d-print-none">
        <div class="d-flex justify-content-between align-items-start flex-wrap">
          <div>
            <h3 class="mb-1">
              <feather-icon
                icon="FileTextIcon"
                size="22"
                class="mr-50"
              />
              {{ invoice.invoice_number }}
            </h3>
            <p class="text-muted mb-0">
              <small>Réf: {{ invoice.reference }} · Créée le {{ formatDate(invoice.created_at) }}</small>
            </p>
          </div>
          <div
            class="d-flex align-items-center"
            style="gap: 0.5rem"
          >
            <b-button
              size="sm"
              variant="outline-secondary"
              class="d-print-none"
              aria-label="Imprimer la facture livreur"
              @click="printInvoice"
            >
              <feather-icon
                icon="PrinterIcon"
                size="14"
                class="mr-50"
              />
              Imprimer
            </b-button>

            <b-badge
              :variant="statusVariant(invoice.status)"
              class="px-3 py-2"
              style="font-size: 0.85rem"
            >
              {{ statusLabel(invoice.status) }}
            </b-badge>

            <!-- Admin Actions -->
            <b-dropdown
              v-if="isAdmin && invoice.status !== 'paid' && invoice.status !== 'cancelled'"
              size="sm"
              variant="outline-primary"
              class="d-print-none"
              right
              no-caret
            >
              <template #button-content>
                <feather-icon
                  icon="SettingsIcon"
                  size="14"
                />
                Actions
              </template>
              <b-dropdown-item
                v-if="invoice.status === 'draft'"
                @click="updateStatus('confirmed')"
              >
                <feather-icon
                  icon="CheckCircleIcon"
                  size="14"
                  class="mr-50 text-info"
                />
                Confirmer
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.status === 'confirmed'"
                @click="updateStatus('paid')"
              >
                <feather-icon
                  icon="DollarSignIcon"
                  size="14"
                  class="mr-50 text-success"
                />
                Marquer payée
              </b-dropdown-item>
              <b-dropdown-divider />
              <b-dropdown-item @click="cancelInvoice">
                <feather-icon
                  icon="XCircleIcon"
                  size="14"
                  class="mr-50 text-warning"
                />
                Annuler
              </b-dropdown-item>
              <b-dropdown-item
                v-if="invoice.status === 'draft'"
                @click="deleteInvoice"
              >
                <feather-icon
                  icon="TrashIcon"
                  size="14"
                  class="mr-50 text-danger"
                />
                Supprimer
              </b-dropdown-item>
            </b-dropdown>
          </div>
        </div>
      </b-card>

      <!-- Compact print summary -->
      <section class="invoice-print-summary">
        <div class="invoice-print-summary-heading">
          <span>Résumé de la facture</span>
          <span>{{ invoice.invoice_number }}</span>
        </div>
        <div class="invoice-print-summary-grid">
          <div>
            <span>Période</span>
            <strong>{{ formatDate(invoice.start_date) }} – {{ formatDate(invoice.end_date) }}</strong>
          </div>
          <div>
            <span>Colis traités</span>
            <strong>{{ invoice.total_deliveries }} × {{ formatAmount(invoice.delivery_commission_rate) }} DT</strong>
          </div>
          <div>
            <span>Commission colis</span>
            <strong>{{ formatAmount(invoice.delivery_commission_amount) }} DT</strong>
          </div>
          <div>
            <span>Ramassages</span>
            <strong>{{ invoice.total_pickups || 0 }} × {{ formatAmount(invoice.pickup_commission_rate) }} DT</strong>
          </div>
          <div class="invoice-print-total">
            <span>Total à payer</span>
            <strong>{{ formatAmount(invoice.total_amount) }} DT</strong>
          </div>
        </div>
      </section>

      <!-- Summary Cards -->
      <b-row class="mb-2 d-print-none">
        <!-- Driver Info -->
        <b-col
          lg="4"
          md="6"
          class="mb-2"
        >
          <b-card class="h-100">
            <h5 class="mb-3">
              <feather-icon
                icon="UserIcon"
                size="18"
                class="mr-50"
              />
              Livreur
            </h5>
            <div class="detail-row">
              <span class="label">Nom</span>
              <span class="value">{{ invoice.driver_name || invoice.driver_username || '-' }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Rôle</span>
              <span class="value">{{ invoice.driver_role || '-' }}</span>
            </div>
          </b-card>
        </b-col>

        <!-- Period Info -->
        <b-col
          lg="4"
          md="6"
          class="mb-2"
        >
          <b-card class="h-100">
            <h5 class="mb-3">
              <feather-icon
                icon="CalendarIcon"
                size="18"
                class="mr-50"
              />
              Période
            </h5>
            <div class="detail-row">
              <span class="label">Du</span>
              <span class="value">{{ formatDate(invoice.start_date) }}</span>
            </div>
            <div class="detail-row">
              <span class="label">Au</span>
              <span class="value">{{ formatDate(invoice.end_date) }}</span>
            </div>
          </b-card>
        </b-col>

        <!-- Financial Summary -->
        <b-col
          lg="4"
          md="12"
          class="mb-2"
        >
          <b-card class="h-100 summary-card">
            <h5 class="mb-3">
              <feather-icon
                icon="DollarSignIcon"
                size="18"
                class="mr-50"
              />
              Résumé financier
            </h5>
            <div
              v-if="invoice.total_deliveries > 0"
              class="detail-row"
            >
              <span class="label">Colis traités</span>
              <span class="value">{{ invoice.total_deliveries }} × {{ formatAmount(invoice.delivery_commission_rate) }} DT</span>
            </div>
            <div
              v-if="invoice.total_deliveries > 0"
              class="detail-row"
            >
              <span class="label">Commission colis</span>
              <span class="value text-success">{{ formatAmount(invoice.delivery_commission_amount) }} DT</span>
            </div>
            <div
              v-if="invoice.total_pickups > 0"
              class="detail-row"
            >
              <span class="label">Ramassages</span>
              <span class="value">{{ invoice.total_pickups }} × {{ formatAmount(invoice.pickup_commission_rate) }} DT</span>
            </div>
            <div
              v-if="invoice.total_pickups > 0"
              class="detail-row"
            >
              <span class="label">Commission ramassage</span>
              <span class="value text-success">{{ formatAmount(invoice.pickup_commission_amount) }} DT</span>
            </div>
            <hr class="my-1">
            <div class="detail-row">
              <span class="label font-weight-bold">Total</span>
              <span
                class="value text-success font-weight-bold"
                style="font-size: 1.1rem"
              >
                {{ formatAmount(invoice.total_amount) }} DT
              </span>
            </div>
          </b-card>
        </b-col>
      </b-row>

      <!-- Delivery Items -->
      <b-card
        v-if="invoice.delivery_items && invoice.delivery_items.length > 0"
        class="mb-2 invoice-delivery-items"
      >
        <h5 class="mb-3">
          <feather-icon
            icon="PackageIcon"
            size="18"
            class="mr-50"
          />
          Colis de la run sheet ({{ invoice.delivery_items.length }})
        </h5>
        <b-table
          :items="invoice.delivery_items"
          :fields="deliveryFields"
          responsive
          hover
          small
          striped
        >
          <template #cell(order_id)="data">
            <span class="font-weight-bold">{{ data.item.order_id || data.item.order }}</span>
          </template>
          <template #cell(order_status_display)="data">
            <b-badge :variant="data.item.order_status === 4 ? 'success' : 'warning'">
              {{ data.item.order_status_display || (data.item.order_status === 4 ? 'Livré' : 'Retour au dépôt') }}
            </b-badge>
          </template>
          <template #cell(runsheet_id)="data">
            <span>{{ data.item.runsheet_id || '-' }}</span>
          </template>
          <template #cell(commission_amount)="data">
            <span class="text-success font-weight-bold">
              {{ formatAmount(data.item.commission_amount) }} DT
            </span>
          </template>
        </b-table>
      </b-card>

      <!-- Pickup Items -->
      <b-card
        v-if="invoice.pickup_items && invoice.pickup_items.length > 0"
        class="mb-2"
      >
        <h5 class="mb-3">
          <feather-icon
            icon="ShoppingCartIcon"
            size="18"
            class="mr-50"
          />
          Ramassages ({{ invoice.pickup_items.length }})
        </h5>
        <b-table
          :items="invoice.pickup_items"
          :fields="pickupFields"
          responsive
          hover
          small
          striped
        >
          <template #cell(commission_amount)="data">
            <span class="text-success font-weight-bold">
              {{ formatAmount(data.item.commission_amount) }} DT
            </span>
          </template>
        </b-table>
      </b-card>

      <!-- Signatures (print only) -->
      <div class="invoice-signatures">
        <div>
          <strong>Signature du responsable</strong>
        </div>
        <div>
          <strong>Signature du livreur</strong>
        </div>
      </div>

      <!-- Back Button -->
      <div class="mt-2 d-print-none">
        <b-button
          variant="outline-secondary"
          :to="{ name: 'driver-invoice-list' }"
        >
          <feather-icon
            icon="ArrowLeftIcon"
            size="14"
            class="mr-50"
          />
          Retour à la liste
        </b-button>
      </div>
    </template>
  </div>
</template>

<script>
import {
  BCard,
  BRow,
  BCol,
  BTable,
  BButton,
  BSpinner,
  BBadge,
  BDropdown,
  BDropdownItem,
  BDropdownDivider,
} from 'bootstrap-vue'
import driverInvoiceApi from '@/libs/driverInvoiceApi'
import storeAuth from '@/store/store'

export default {
  name: 'DriverInvoiceDetail',
  components: {
    BCard,
    BRow,
    BCol,
    BTable,
    BButton,
    BSpinner,
    BBadge,
    BDropdown,
    BDropdownItem,
    BDropdownDivider,
  },
  data() {
    return {
      loading: false,
      invoice: null,
      company: {},
      deliveryFields: [
        { key: 'order_id', label: 'N° Colis' },
        { key: 'order_name', label: 'Nom' },
        { key: 'order_supplier_name', label: 'Expéditeur' },
        { key: 'completion_date', label: 'Date', formatter: this.formatDate },
        { key: 'order_status_display', label: 'Statut', class: 'text-center' },
        { key: 'runsheet_id', label: 'Run sheet', class: 'text-center' },
        { key: 'commission_amount', label: 'Commission', class: 'text-right' },
      ],
      pickupFields: [
        { key: 'pickup_id', label: 'N° Ramassage' },
        { key: 'pickup_supplier_name', label: 'Expéditeur' },
        { key: 'completion_date', label: 'Date', formatter: this.formatDate },
        { key: 'order_count', label: 'Colis', class: 'text-center' },
        { key: 'commission_amount', label: 'Commission', class: 'text-right' },
      ],
    }
  },
  computed: {
    isAdmin() {
      const role = this.$store.state.user?.role || this.$store.state.role || localStorage.getItem('role')
      return ['admin', 'superadmin', 'admin_developer', 'accountant'].includes(role)
    },
  },
  mounted() {
    this.company = storeAuth.state.company || {}
    this.loadInvoice()
  },
  methods: {
    async loadInvoice() {
      this.loading = true
      try {
        const response = await driverInvoiceApi.get(this.$route.params.id)
        this.invoice = response.data
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error('Error loading invoice:', error)
        this.toast('Erreur lors du chargement de la facture', 'danger')
      } finally {
        this.loading = false
      }
    },

    async updateStatus(newStatus) {
      const labels = { confirmed: 'confirmer', paid: 'marquer comme payée' }
      const ok = await this.$bvModal.msgBoxConfirm(
        `Voulez-vous ${labels[newStatus]} cette facture ?`,
        {
          title: 'Confirmation',
          okVariant: newStatus === 'paid' ? 'success' : 'primary',
          okTitle: 'Oui',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        const response = await driverInvoiceApi.updateStatus(this.invoice.id, newStatus)
        this.invoice = { ...this.invoice, ...response.data }
        this.toast('Statut mis à jour', 'success')
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la mise à jour'
        this.toast(msg, 'danger')
      }
    },

    async cancelInvoice() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous annuler cette facture ? Les colis redeviendront éligibles.',
        {
          title: 'Annuler la facture',
          okVariant: 'warning',
          okTitle: 'Oui, annuler',
          cancelTitle: 'Non',
        },
      )
      if (!ok) return
      try {
        const response = await driverInvoiceApi.cancel(this.invoice.id)
        this.invoice = { ...this.invoice, ...response.data }
        this.toast('Facture annulée', 'success')
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de l\'annulation'
        this.toast(msg, 'danger')
      }
    },

    async deleteInvoice() {
      const ok = await this.$bvModal.msgBoxConfirm(
        'Voulez-vous supprimer définitivement cette facture ?',
        {
          title: 'Supprimer la facture',
          okVariant: 'danger',
          okTitle: 'Supprimer',
          cancelTitle: 'Annuler',
        },
      )
      if (!ok) return
      try {
        await driverInvoiceApi.delete(this.invoice.id)
        this.toast('Facture supprimée', 'success')
        this.$router.push({ name: 'driver-invoice-list' })
      } catch (error) {
        const msg = error.response?.data?.error || 'Erreur lors de la suppression'
        this.toast(msg, 'danger')
      }
    },

    statusVariant(status) {
      const map = {
        draft: 'secondary',
        confirmed: 'info',
        paid: 'success',
        cancelled: 'danger',
      }
      return map[status] || 'secondary'
    },

    statusLabel(status) {
      const map = {
        draft: 'Brouillon',
        confirmed: 'Confirmée',
        paid: 'Payée',
        cancelled: 'Annulée',
      }
      return map[status] || status
    },

    formatAmount(value) {
      if (value === null || value === undefined) return '0.000'
      return parseFloat(value).toFixed(3)
    },

    formatDate(dateStr) {
      if (!dateStr) return ''
      return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
      })
    },

    async printInvoice() {
      await this.$nextTick()
      if (document.fonts && document.fonts.ready) await document.fonts.ready

      const images = Array.from(
        document.querySelectorAll('.invoice-print-document-header img'),
      )
      await Promise.all(images.map(image => {
        if (image.complete) return Promise.resolve()
        return new Promise(resolve => {
          image.addEventListener('load', resolve, { once: true })
          image.addEventListener('error', resolve, { once: true })
        })
      }))

      window.print()
    },

    toast(message, variant) {
      this.$bvToast.toast(message, {
        title: variant === 'success' ? 'Succès' : 'Erreur',
        variant,
        solid: true,
        autoHideDelay: 4000,
      })
    },
  },
}
</script>

<style scoped>
.detail-row {
  display: flex;
  justify-content: space-between;
  padding: 6px 0;
  border-bottom: 1px solid #f0f0f0;
}

.detail-row:last-child {
  border-bottom: none;
}

.detail-row .label {
  color: #6c757d;
  font-size: 0.9rem;
}

.detail-row .value {
  font-weight: 500;
  font-size: 0.9rem;
}

.summary-card {
  background: linear-gradient(135deg, #f8f9fa 0%, #fff 100%);
}

.invoice-signatures {
  display: none;
}

.invoice-print-document-header,
.invoice-print-summary {
  display: none;
}
</style>

<style lang="scss">
@media print {
  @page {
    size: A4 portrait;
    margin: 8mm 10mm 10mm;
  }

  html,
  body {
    height: auto !important;
    background: #fff !important;
  }

  body * {
    visibility: hidden;
  }

  .driver-invoice-detail,
  .driver-invoice-detail * {
    visibility: visible;
  }

  .header-navbar,
  .main-menu,
  .content-header,
  footer,
  .invoice-delivery-items,
  .d-print-none {
    display: none !important;
  }

  .app-content,
  .content-wrapper,
  .content-body,
  .driver-invoice-detail {
    width: 100% !important;
    max-width: none !important;
    margin: 0 !important;
    padding: 0 !important;
  }

  .driver-invoice-detail {
    position: absolute !important;
    top: 0 !important;
    left: 0 !important;
  }

  .driver-invoice-detail .card {
    box-shadow: none !important;
    border: 1px solid #cfd4da !important;
    break-inside: avoid;
  }

  .driver-invoice-detail .card-body {
    padding: 0.75rem !important;
  }

  .driver-invoice-detail .table-responsive {
    overflow: visible !important;
  }

  .driver-invoice-detail table {
    width: 100% !important;
    border-collapse: collapse !important;
    font-size: 9pt;
  }

  .driver-invoice-detail th,
  .driver-invoice-detail td {
    padding: 5px !important;
    border: 1px solid #d8dce0 !important;
  }

  .driver-invoice-detail .badge,
  .driver-invoice-detail .text-success,
  .driver-invoice-detail .text-primary {
    color: #000 !important;
  }

  .invoice-print-document-header,
  .invoice-print-summary {
    display: block !important;
  }

  .invoice-print-document-header {
    margin: 0 0 4mm;
  }

  .invoice-print-title-row {
    display: none !important;
  }

  .invoice-print-kicker {
    margin-bottom: 1mm;
    font-size: 9pt;
    font-weight: 700;
    letter-spacing: 1.6px;
    text-transform: uppercase;
  }

  .invoice-print-title-row h1 {
    margin: 0 0 1mm !important;
    font-size: 21pt !important;
    line-height: 1.1 !important;
  }

  .invoice-print-reference {
    font-size: 9pt;
    color: #444 !important;
  }

  .invoice-print-status {
    padding: 2mm 4mm;
    font-size: 9pt;
    font-weight: 700;
    text-transform: uppercase;
    border: 1px solid #111;
    border-radius: 2px;
  }

  .invoice-print-parties {
    display: grid !important;
    grid-template-columns: 1fr 1fr;
    gap: 6mm;
  }

  .invoice-print-party {
    min-height: 56mm;
    padding: 5mm;
    border: 1.4px solid #7f8994;
    border-radius: 3px;
    break-inside: avoid;
  }

  .invoice-print-party h2 {
    margin: 0 0 2.5mm !important;
    font-size: 17pt !important;
    line-height: 1.15 !important;
    color: #111 !important;
  }

  .invoice-print-party p {
    margin: 0 0 1.7mm !important;
    font-size: 12pt;
    line-height: 1.3;
    color: #111 !important;
  }

  .invoice-print-logo {
    display: block !important;
    visibility: visible !important;
    opacity: 1 !important;
    width: 42mm !important;
    height: auto;
    max-width: none !important;
    max-height: none !important;
    margin: 0 0 3mm !important;
    object-fit: contain !important;
    object-position: left center !important;
  }

  .invoice-print-party-label {
    margin-bottom: 2.5mm;
    font-size: 10pt;
    font-weight: 700;
    letter-spacing: 1px;
    text-transform: uppercase;
    color: #555 !important;
  }

  .invoice-print-summary {
    margin-bottom: 6mm;
    border: 1.4px solid #707b86;
    border-radius: 3px;
    break-inside: avoid;
  }

  .invoice-print-summary-heading {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 2.5mm 4mm;
    font-size: 11pt;
    font-weight: 700;
    text-transform: uppercase;
    background: #eef0f2 !important;
    border-bottom: 1px solid #8f98a3;
  }

  .invoice-print-summary-grid {
    display: grid !important;
    grid-template-columns: repeat(4, 1fr);
  }

  .invoice-print-summary-grid > div {
    padding: 3mm 4mm;
    border-right: 1px solid #d4d8dc;
  }

  .invoice-print-summary-grid > div:nth-child(4) {
    border-right: 0;
  }

  .invoice-print-summary-grid span,
  .invoice-print-summary-grid strong {
    display: block;
  }

  .invoice-print-summary-grid span {
    margin-bottom: 1mm;
    font-size: 10pt;
    color: #333 !important;
  }

  .invoice-print-summary-grid strong {
    font-size: 12pt;
    color: #000 !important;
  }

  .invoice-print-summary-grid .invoice-print-total {
    grid-column: 1 / -1;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 3mm 4mm;
    border-top: 1px solid #8f98a3;
    border-right: 0;
    background: #f6f7f8 !important;
  }

  .invoice-print-summary-grid .invoice-print-total span,
  .invoice-print-summary-grid .invoice-print-total strong {
    margin: 0;
    font-size: 12pt;
  }

  .invoice-signatures {
    display: flex !important;
    justify-content: space-between;
    margin-top: 25mm;
    padding: 0 15mm;
    break-inside: avoid;
  }

  .invoice-signatures > div {
    min-width: 55mm;
    padding-top: 3mm;
    text-align: center;
    border-top: 1px solid #000;
    font-size: 10pt;
  }
}
</style>
